import { NewBornGranularity, NewBornTimelinePointDTO } from '../../dto/newborn/NewBornDTO';
import { markPeaks, PeakAnalysis } from '../ServicesStatistics/peaks';
import { countDays, parseIsoDate, TimelinePoint } from '../ServicesStatistics/statisticsUtils';

/**
 * Evolución de los nacimientos por semana o mes. Funciones puras, sin React: se calculan en el front con los puntos que ya
 * llegaron de `/timeline`.
 */

/** Lo que se mide en el gráfico: el mismo dato en otra vista. */
export type NewBornMeasure = 'births' | 'cesarean';

export const NEWBORN_MEASURES: NewBornMeasure[] = ['births', 'cesarean'];

/**
 * Partos mínimos para que el % de cesáreas de un período se tome en serio (marcador pleno y chance de ser pico). A unos 2
 * nacimientos por día, una semana ronda 14 partos y un mes 60: con menos de 10 un solo parto mueve el porcentaje 10 puntos
 * o más. Es más bajo que el mínimo de los indicadores (`minCasesForVerdict`, 30) a propósito: con 30, ninguna semana
 * llegaría y todo el gráfico semanal quedaría hueco; aquel mínimo es para decir «mejor» o «peor» de una comparación, éste
 * para dibujar y marcar un período. A VALIDAR por el servicio.
 */
export const EVOLUTION_MIN_CASES = 10;

/** Cantidad de días del rango hasta la cual el backend agrupa por semana (con más, por mes). */
export const WEEK_MAX_DAYS = 120;

/** La agrupación que elige el backend para un rango (`yyyy-MM-dd`), para mostrarla en el selector antes de que lleguen los datos. */
export function autoGranularity(from: string, to: string): NewBornGranularity {
    return countDays(parseIsoDate(from), parseIsoDate(to)) <= WEEK_MAX_DAYS ? 'WEEK' : 'MONTH';
}

/** La agrupación de lo que llegó: las claves de mes son `yyyy-MM` y las de semana `yyyy-MM-dd`. `null` sin puntos. */
export function granularityOf(points: NewBornTimelinePointDTO[]): NewBornGranularity | null {
    return points.length === 0 ? null : points[0].period.length === 7 ? 'MONTH' : 'WEEK';
}

/** El valor de la medida en el período; null si no hay con qué calcularlo (un % sin partos no es 0 %). */
export function pointValue(point: NewBornTimelinePointDTO, measure: NewBornMeasure): number | null {
    return measure === 'births' ? point.births : point.cesareanPct;
}

/** La base del valor: nacimientos para la cantidad, partos para el % de cesáreas. */
export function pointBase(point: NewBornTimelinePointDTO, measure: NewBornMeasure): number {
    return measure === 'births' ? point.births : point.cesareanBase;
}

/**
 * Período que se dibuja con marcador hueco: recortado por el rango (parcial) o, con el % de cesáreas, con menos de
 * `EVOLUTION_MIN_CASES` partos. La cantidad de nacimientos no tiene este problema: un período con menos nacimientos es
 * exactamente lo que dice.
 */
export function isLowVolume(point: NewBornTimelinePointDTO, measure: NewBornMeasure): boolean {
    return point.partial || (measure === 'cesarean' && point.cesareanBase > 0 && point.cesareanBase < EVOLUTION_MIN_CASES);
}

/**
 * Picos del % de cesáreas con el criterio de `markPeaks` (media + 2 desvíos entre los períodos que cuentan, al menos 7
 * períodos que cuentan, como mucho 3) y partos como base: cuentan los períodos con al menos `EVOLUTION_MIN_CASES` partos y
 * los parciales no cuentan (son más cortos que los demás). Con «Nacimientos» no hay picos: el volumen sube y baja sin ser
 * un problema.
 */
export function cesareanPeaks(points: NewBornTimelinePointDTO[]): PeakAnalysis {
    return markPeaks(
        points.map((point) => ({ value: point.partial ? null : point.cesareanPct, attentions: point.cesareanBase })),
        EVOLUTION_MIN_CASES,
    );
}

/** Días que dura el período completo (con el recorte del rango no cuenta): 7 la semana, los del mes el mes. */
function fullLengthDays(point: NewBornTimelinePointDTO, granularity: NewBornGranularity): number {
    if (granularity === 'WEEK') return 7;
    const start = parseIsoDate(point.from);
    return new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
}

/** Los totales del rango sumando los períodos: lo que se compara contra un período y la línea de promedio del gráfico. */
export interface RangeReference {
    /** Nacimientos de un período completo promedio (cada período pesa por los días que cubre del rango). */
    birthsPerPeriod: number | null;
    /** Porcentajes del rango entero, calculados sobre las sumas (nunca promediando porcentajes); null sin base. */
    cesareanPct: number | null;
    pretermPct: number | null;
    lowWeightPct: number | null;
    /** Las bases de esos porcentajes en el rango: sin el mínimo de casos no hay veredicto contra ellos. */
    cesareanBase: number;
    pretermBase: number;
    lowWeightBase: number;
}

const share = (count: number, base: number): number | null => (base > 0 ? Math.round((count / base) * 1000) / 10 : null);

export function rangeReference(points: NewBornTimelinePointDTO[], granularity: NewBornGranularity): RangeReference {
    const total = (pick: (point: NewBornTimelinePointDTO) => number) => points.reduce((sum, point) => sum + pick(point), 0);
    // Un período recortado cuenta por la parte que cubre: 3 días de una semana pesan 3/7
    const covered = points.reduce(
        (sum, point) => sum + countDays(parseIsoDate(point.from), parseIsoDate(point.to)) / fullLengthDays(point, granularity),
        0,
    );
    return {
        birthsPerPeriod: covered > 0 ? total((point) => point.births) / covered : null,
        cesareanPct: share(total((point) => point.cesareans), total((point) => point.cesareanBase)),
        pretermPct: share(total((point) => point.preterm), total((point) => point.pretermBase)),
        lowWeightPct: share(total((point) => point.lowWeight), total((point) => point.lowWeightBase)),
        cesareanBase: total((point) => point.cesareanBase),
        pretermBase: total((point) => point.pretermBase),
        lowWeightBase: total((point) => point.lowWeightBase),
    };
}

/** El promedio que dibuja la línea de la medida: nacimientos por período o el % de cesáreas del rango. */
export function evolutionAverage(points: NewBornTimelinePointDTO[], measure: NewBornMeasure, granularity: NewBornGranularity): number | null {
    const reference = rangeReference(points, granularity);
    return measure === 'births' ? reference.birthsPerPeriod : reference.cesareanPct;
}

/** El período como `TimelinePoint` de Estadísticas, para reusar sus rótulos de eje y títulos (con las fechas efectivas). */
export function toTimelinePoint(point: NewBornTimelinePointDTO): TimelinePoint {
    return {
        period: point.period,
        start: parseIsoDate(point.from),
        end: parseIsoDate(point.to),
        attentions: point.births,
        avgWait: null,
        avgService: null,
    };
}
